import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Col,
  Row,
  Card,
  Table,
  Button,
  Spinner,
} from "@themesberg/react-bootstrap";
import { useCookies } from "react-cookie";
import TableRow from "../component/TableRow";
import PairTicketRow from "../component/PairTicketRow";
import { calculatePage } from "../../../../helper/helper";
import Pagination from "../../../../components/Pagination";
import { Context } from "../TicketStorage";
import AddTicketPopup from "../sub-screen/add-ticket-popup/AddTicketPopup";
import EditTicketPopup from "../sub-screen/EditTicketPopup";
import {
  getListTicket,
  confirmInput,
} from "../../../../controller/api/__classic__/input";

const groupTickets = (tickets) => {
  if (!tickets || tickets.length === 0)
    return { singleTickets: [], pairGroups: [] };

  const singleTickets = [];
  const pairMap = new Map();

  for (const ticket of tickets) {
    if (ticket.isSingle === false) {
      const key = ticket.last5Numbers;
      if (!pairMap.has(key)) {
        pairMap.set(key, []);
      }
      pairMap.get(key).push(ticket);
    } else {
      singleTickets.push(ticket);
    }
  }

  const pairGroups = Array.from(pairMap.entries()).map(
    ([last5Numbers, groupedTickets]) => ({
      last5Numbers,
      tickets: groupedTickets,
      representative: groupedTickets[0],
      totalTickets: groupedTickets.length,
    }),
  );

  pairGroups.sort((a, b) => b.last5Numbers.localeCompare(a.last5Numbers));

  return { singleTickets, pairGroups };
};

const _Table = () => {
  const { state } = useContext(Context);

  const [cookies] = useCookies(["lottmekong"]);

  const [data, setData] = useState({});

  const [resultInfo, setResultInfo] = useState(null);

  const [page, setPage] = useState(0);

  const [addTicketPopupShow, setAddTicketPopupShow] = useState(false);

  const [editTicketPopupShow, setEditTicketPopupShow] = useState(false);

  const [selectedTicket, setSelectedTicket] = useState(null);

  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState(0);

  const [isDirectSale, setIsDirectSale] = useState(false);

  const resultIdRef = useRef(null);

  const { singleTickets, pairGroups } = useMemo(
    () => groupTickets(data.list),
    [data.list],
  );

  const getAllFunc = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getListTicket(cookies.lottmekong, state.inputId);

      if (response) {
        if (response.resultInfo) {
          setStatus(response.resultInfo.status || 0);
        }
        const res = {
          list: response.data,
          total: response.total,
          totalPage: calculatePage(response.total),
        };
        setData(res || {});
        setResultInfo(response.resultInfo || null);
      }
    } finally {
      setLoading(false);
    }
  }, [cookies.lottmekong, state.inputId]);

  useEffect(() => {
    if (state.inputId !== "") {
      getAllFunc();
    }
  }, [getAllFunc, state.inputId]);

  const onConfirmInput = async () => {
    const isConfirmed = window.confirm(
      "Vé sẽ được đăng bán ngay lập tức. Bạn có chắc chắn không?",
    );

    if (!isConfirmed) {
      return;
    }

    const response = await confirmInput(cookies.lottmekong, state.inputId);

    if (response) {
      window.location.reload();
    }
  };

  return (
    <>
      {loading && (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Đang tải...</span>
          </Spinner>
        </div>
      )}
      {!loading && resultInfo && (
        <Card border="light" className="shadow-sm mb-3">
          <Card.Body>
            <Row>
              <Col md={4}>
                <strong>Mã kỳ:</strong> {resultInfo.id}
              </Col>
              <Col md={4}>
                <strong>Kênh:</strong>{" "}
                {resultInfo.ClassicLotteryChannel?.name || resultInfo.channelId}
              </Col>
              <Col md={4}>
                <strong>Ngày quay số:</strong> {resultInfo.resultDate}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
      {!loading && (
        <>
          {status >= 4 ? (
            <div className="mb-3">
              <span style={{ color: "#17a2b8", fontWeight: 600 }}>
                Trạng thái: Đã tính thưởng
              </span>
            </div>
          ) : status === 1 ? (
            <div className="mb-3">
              <span
                style={{ color: "#1cc88a", fontWeight: 600, marginRight: 20 }}
              >
                Trạng thái: Đang bán
              </span>
              <Button
                variant="success"
                onClick={() => {
                  const isConfirmed = window.confirm(
                    "Vé nhập lên sẽ được bán luôn. Bạn có chắc chắn không?",
                  );
                  if (!isConfirmed) return;
                  resultIdRef.current = state.inputId;
                  setIsDirectSale(true);
                  setAddTicketPopupShow(true);
                }}
              >
                Thêm vé đơn
              </Button>
            </div>
          ) : (
            <>
              <Button
                className="mb-3"
                style={{ marginRight: 20 }}
                variant="success"
                type="submit"
                onClick={() => {
                  resultIdRef.current = state.inputId;
                  setAddTicketPopupShow(true);
                }}
              >
                Thêm vé
              </Button>
              <Button
                className="mb-3"
                variant="warning"
                onClick={onConfirmInput}
              >
                Bán ngay
              </Button>
            </>
          )}
          <Card border="light" className="shadow-sm">
            <Table
              responsive
              className="align-items-center table-bordered table-hover"
            >
              <thead className="text-center">
                <tr>
                  <th scope="col">STT</th>
                  <th scope="col">Loại</th>
                  <th scope="col">Dãy số</th>
                  <th scope="col">Số lượng</th>
                  <th scope="col">Hình</th>
                  <th scope="col">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {pairGroups.map((group, index) => (
                  <PairTicketRow
                    cookies={cookies}
                    resultId={state.inputId}
                    onRefresh={getAllFunc}
                    onEditTicket={(ticket) => {
                      setSelectedTicket(ticket);
                      setEditTicketPopupShow(true);
                    }}
                    rIndex={index}
                    key={`pair-group-${group.last5Numbers}`}
                    pairGroup={group}
                    resultStatus={status}
                  />
                ))}
                {singleTickets.map((pv, index) => (
                  <TableRow
                    cookies={cookies}
                    resultId={state.inputId}
                    onRefresh={getAllFunc}
                    onEdit={() => {
                      setSelectedTicket(pv);
                      setEditTicketPopupShow(true);
                    }}
                    rIndex={pairGroups.length + index}
                    key={`table-keys-lottmekong-${pv.id}`}
                    {...pv}
                  />
                ))}
              </tbody>
            </Table>
            <Card.Footer className="flex-row">
              <Row className="align-items-center">
                <Col>
                  <Pagination
                    totalPage={data.totalPage || 0}
                    currentPage={page || 0}
                    onPageChange={(i) => {
                      setPage(i);
                    }}
                  />
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        </>
      )}
      <AddTicketPopup
        region={resultInfo?.ClassicLotteryChannel?.region || "MN"}
        show={addTicketPopupShow}
        id={resultIdRef.current}
        channelName={resultInfo?.ClassicLotteryChannel?.name}
        resultDate={resultInfo?.resultDate}
        isDirectSale={isDirectSale}
        onHide={() => {
          resultIdRef.current = null;
          setIsDirectSale(false);
          setAddTicketPopupShow(false);
        }}
        onSuccess={() => {
          getAllFunc();
        }}
      />
      <EditTicketPopup
        show={editTicketPopupShow}
        onHide={() => {
          setSelectedTicket(null);
          setEditTicketPopupShow(false);
        }}
        ticketData={selectedTicket}
        cookies={cookies}
        resultId={state.inputId}
        onRefresh={getAllFunc}
      />
    </>
  );
};

export default React.memo(_Table);
